// Story 9.4 (ADO-1948): "Previous orders", the account order-history list.
//
// Figma "Previous Orders" 4746:42736: a title block with a Return to products action,
// the Filters panel, a Sort bar, a vertical list of order cards and a pagination footer.
// Page chrome follows MyAccount (the full SiteHeader over the screen's own <main>), NOT
// AppShell, which is the admin-ops chrome.
//
// THE CARD HEAD (Order cards 4594:13876) packs its summary to the LEFT: the reference,
// then the labelled Order date, Placed by, Total and Deposit total fields, with the Track
// control and the expander at the right edge. Every card lays those fields on the same
// column tracks, so they line up down the list. Order status is backend-only here: the
// card has no status chip and the open panel no "Invoiced" note. `status` and `invoiced`
// stay on the wire, and the client still guards them, but neither is displayed.
//
// THE SORT BAR (5206:7400) sits above the list: "Showing X–Y of Z" from the page
// metadata, and a "Sort by" design-system `Listbox` (newest first, the default; oldest
// first; highest total; lowest total). The sort is applied SERVER-side in the window
// read, because the list is paged there and a client sort would only reorder one page.
// The bar renders with the list only, never in the empty or no-results states.
//
// Story 9.5 (ADO-1949): the per-row expander opens an inline panel that reads the order in
// full over `GET /api/v1/orders/{orderId}` and renders the line table (name over code,
// price, deposit, qty, deposit total, line total) plus a "Total cost" footer.
//
// Story 9.3 (ADO-1081): the AC1 tracking control, labelled "Track order" as the
// ADO-1779 frames draw it, lives on the card head, so it is
// reachable with the panel shut. AC1 offers the option "when they view the order history",
// so its eligibility cannot wait for the detail read: it is `OrderSummaryDto.trackable`,
// derived by the SAME `isOrderTrackable` gate the detail read applies. It renders only for
// a track-eligible order and never for a supplier user, fetches the external
// `bwg.depot.guru` deep link and opens it in a new tab.
//
// Story 9.6 (ADO-1080): the Filters panel (`OrderFilters.tsx`) narrows by submission-date
// range, order reference and a sales-rep-orders switch, enforced SERVER-side (AC7);
// the panel only renders and reports. The screen's whole request (page, page size, sort
// and the four filters) is read from the query string on every render (AC8), so copying
// the address, reloading it or walking browser back/forward restores the view with
// nothing re-entered. The sort is not a filter: it survives Clear filters and a depot
// switch, and it does not count toward the applied-filter count.
//
// Story 8.7 (ADO-2016) ADDS working from a previous order (`ReAddOrder.tsx`): the expanded
// card closes with the Re-add band (Figma 4594:13736), "Add order to cart" and "Replace cart
// with order" behind their confirm modals. Both take the whole order at the ordered
// quantities, so the line table stays read-only (Figma 4925:50633). The band
// is not offered on a PRESELL order (D-12). The per-page 10/20/50/100 selector ships:
// the 2026-09-18 design round took the pager from the catalogue listing whole, and the
// route accepts those four sizes.
//
// ADO-1779 adds the tablet (768-1023, Figma 5412:60428) and mobile (<768, 5412:21684)
// frames. Tablet keeps this composition, re-spaced. Mobile moves the Filters panel and
// the Sort by popup into bottom sheets, gives the Track control its own row under the
// card head, lists each line as a block of labelled figures instead of the table, and
// confirms the re-add actions in bottom sheets.
//
// WHAT IT RENDERS DIFFERENTLY FROM THE FRAME, and why:
//   - The reference is `customerOrderReference` when the order carries one, else
//     ShopLink's `orderReference` (`ORD-<n>`, the number the "Order sent" page shows), else
//     the `orderId` for an order that has neither.
//   - "Placed by" is the sender's NAME (AC3), resolved from membership and batched once
//     per page. The frame shows an account code; a name is what the criterion asks for.
//     An unresolved subject falls back to the subject itself.
//
// The Order date is `formatSubmittedDate`'s DD/MM/YYYY in the viewer's own zone with no
// zone label (PO, 2026-09-18); the full UTC instant stays on the `<time dateTime>`
// attribute for machines.
//
// The list is depot-scoped: a shopkeeper looks at one depot's book at a time, and
// the read takes the active depot's uuid. With no active depot the screen prompts
// for one instead of reading, because Story 8.1 deliberately leaves a multi-depot
// user with no default and an undefined depot is not a query.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import {
  Banner,
  Button,
  Collapse,
  Icon,
  Listbox,
  Pagination,
  todayIsoDay,
  useHeaderLayout,
  type HeaderLayout,
  type ListboxOption,
} from '../../design-system';
import {
  NO_ORDER_FILTERS,
  OrderFilters,
  appliedFilterCount,
  type OrderFilterValues,
} from './OrderFilters';
import {
  parseOrderHistoryUrlState,
  sameFilters,
  writeOrderHistoryUrlState,
} from './orderHistoryUrlState';
import { useActiveDepotAccent, useHeaderChrome } from '../Products/useHeaderChrome';
import { depotIconSrc } from '../Choose/depotAccent';
import { CatalogueLinkingHeader } from '../Products/CatalogueLinkingHeader';
import { useSessionDepotId } from '../../availability/session-depot';
import { useSessionCartRange, useSessionCartTarget } from '../../basket/sessionCart';
import { useSessionStore } from '../../auth/useSessionStore';
import { isAdminRole, isSupplierRole } from '../../auth/rbac';
import {
  ORDER_HISTORY_SORTS,
  getOrderDetail,
  getOrderTrackingUrl,
  listOrderHistory,
  type OrderDetail,
  type OrderDetailLine,
  type OrderHistoryPage,
  type OrderHistorySort,
  type OrderSummary,
} from '../../api/orders.client';
import { formatMoney } from '../../shared/money';
import { formatSubmittedDate } from '../../shared/datetime';
import { ReAddBand } from './ReAddOrder';
import styles from './OrderHistory.module.css';

type LoadState = 'loading' | 'loaded' | 'error';

/**
 * The sizes the per-page control offers. Mirrors the catalogue listing's options
 * and the route's `ORDER_HISTORY_PAGE_SIZE_OPTIONS` allow-list: a size outside it
 * is a 400, so the control must not offer one. Hand-written here because apps/web
 * imports no `@shoplink` module package (the audit-log client precedent).
 */
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;

/** The server's default, and what the control shows before the first page lands. */
const DEFAULT_PAGE_SIZE = 10;

/** The Sort by labels (Figma "Sort menu" 5206:7666). A `Record` over the sort type, so a
 *  sort the route accepts cannot be left without a label. */
const SORT_LABELS: Readonly<Record<OrderHistorySort, string>> = {
  newest: 'Newest first',
  oldest: 'Oldest first',
  totalDesc: 'Highest total',
  totalAsc: 'Lowest total',
};

/** The Sort by options, in the route's own order. */
const SORT_OPTIONS: readonly ListboxOption<OrderHistorySort>[] = ORDER_HISTORY_SORTS.map(
  (value) => ({ value, label: SORT_LABELS[value] }),
);

export function OrderHistory() {
  // The active depot's name, for the page subtitle.
  const { depotName } = useHeaderChrome();
  // ADO-1779: the pill is drawn in the active depot's own accent and icon, the ones the
  // header switcher and the depot choice screen give it. Without an ordering entry
  // (before the choices load, or a user with none) it stays neutral, with no icon.
  const depotAccent = useActiveDepotAccent();
  // Every other SiteHeader screen passes this, so omitting it would make /orders the
  // one page where an admin silently loses the Manager Dashboard link.
  const role = useSessionStore((s) => s.role);
  // ADO-1779: the screen's mobile (<768) and tablet (768-1023) frames move controls
  // rather than only reflowing them (the Filters sheet, the Sort sheet, the stacked line
  // blocks), so the layout is read in JS, the same signal the header uses.
  const layout = useHeaderLayout();
  const mobile = layout === 'mobile';

  // The uuid-guarded active depot. `null` means "no depot chosen", which is a
  // prompt, never a read with an undefined parameter.
  const depotId = useSessionDepotId();
  // Story 8.4 (ADO-2013), D-1: the list, an order's detail and its tracking link follow the
  // Trading Account the Shopper chose at the depot (`null` for staff, or none chosen).
  const customerAccountCode = useSessionCartTarget()?.customerAccountCode ?? null;
  // Enabler E8-G (ADO-3109): and the cart's range, so the list shows that range's orders on their
  // own (Chill and Fruit & Veg share depot 25's id), as SL1.0 shows each environment's. Read as
  // primitives so the effect re-runs only on a change of range.
  const range = useSessionCartRange();
  const rangeCode = range?.assortmentCode ?? null;
  const rangeEntryKind = range?.entryKind ?? null;

  // Story 9.6 (ADO-1080), AC8: page, page size, sort AND the filters live in the URL, so
  // a copied, reloaded or back-navigated address restores the same view. Story 9.4 held
  // page in local `useState`; this replaces that store rather than adding a second one,
  // because two sources for the same number is how an address bar and a screen come to
  // disagree.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlState = useMemo(
    () => parseOrderHistoryUrlState(searchParams, PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE),
    [searchParams],
  );
  const { pageSize, sort, filters } = urlState;

  /**
   * The Story 9.4 depot-switch page reset, preserved through the move to the URL.
   *
   * A depot switch is a different set of orders, so carrying page 3 across would open
   * the new depot on a window that may not exist. The reset is applied DURING RENDER
   * (React's supported adjust-state-on-change pattern) rather than from an effect,
   * which is what guarantees the fetch below never runs once with the new depot and the
   * old page — an effect would let exactly one such render through. Writing it to the
   * URL is a side effect and so cannot happen here; the override below holds the reset
   * from this render onward, and an effect syncs the address bar a moment later.
   *
   * ONLY a move between two REAL depots counts. `useSessionStore.activeDepot` starts
   * `null` and becomes a uuid only once the session fetch lands, so on a cold load this
   * screen mounts with no depot and the depot arrives a tick later. Treating that
   * `null -> real` transition as a switch would reset the page and rewrite `page` out of
   * the URL with `replace` — silently destroying a deep-linked `/orders?page=3` on the
   * very case AC8 names first, a RELOAD, with Back unable to recover it because the
   * entry was replaced rather than pushed. So the comparison is against the last
   * NON-NULL depot: `null` is ignored on the way in and on the way out, which also
   * means a genuine A -> null -> B re-hydration still resets, because the gap never
   * overwrote the A it is compared against.
   *
   * The page SIZE is deliberately kept across a depot switch: it is a display
   * preference, not a position in a result set. So are the sort and the filters: a
   * shopkeeper comparing the same window across two depots should not have to re-enter
   * them. The reset below deletes `page` alone, which is what keeps them.
   */
  // Story 8.4 (ADO-2013): another account at the same depot is another set of orders too, so
  // the comparison is on the depot AND the account. The account arriving at the same depot
  // (the ordering choices landing after the session) is hydration, like the depot arriving.
  // Enabler E8-G (ADO-3109): another range at the same depot (Chill to Fruit & Veg) is another set
  // of orders too; the range arriving with the ordering choices is hydration, like the account.
  const [lastRealDepot, setLastRealDepot] = useState<RealDepot | null>(
    depotId === null ? null : { depotId, customerAccountCode, rangeCode },
  );
  const [pageResetPending, setPageResetPending] = useState(false);
  if (
    depotId !== null &&
    (lastRealDepot === null ||
      lastRealDepot.depotId !== depotId ||
      lastRealDepot.customerAccountCode !== customerAccountCode ||
      lastRealDepot.rangeCode !== rangeCode)
  ) {
    setLastRealDepot({ depotId, customerAccountCode, rangeCode });
    const sameDepot = lastRealDepot !== null && lastRealDepot.depotId === depotId;
    const accountArrived =
      sameDepot &&
      lastRealDepot.customerAccountCode === null &&
      lastRealDepot.rangeCode === rangeCode;
    const rangeArrived =
      sameDepot &&
      lastRealDepot.rangeCode === null &&
      (lastRealDepot.customerAccountCode === customerAccountCode ||
        lastRealDepot.customerAccountCode === null);
    // Only when we are LEAVING a real depot (or account, or range). Arriving at the first one is
    // hydration.
    if (lastRealDepot !== null && !accountArrived && !rangeArrived) setPageResetPending(true);
  }
  const page = pageResetPending ? 1 : urlState.page;

  useEffect(() => {
    if (!pageResetPending) return;
    // Clear the override only once the URL agrees, so `page` reads 1 throughout rather
    // than flicking back to the stale number for a render between the two updates.
    if (!searchParams.has('page')) {
      setPageResetPending(false);
      return;
    }
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete('page');
        return next;
      },
      // `replace`: the depot switch already pushed its own history entry, and a second
      // one for the page reset would make browser Back return to the new depot on the
      // old page — the very state this reset exists to prevent.
      { replace: true },
    );
  }, [pageResetPending, searchParams, setSearchParams]);

  /** Today, fixed for the life of the screen so the calendar's ring cannot move under
   *  an open panel, and so a spec can pin it with fake timers at mount. */
  const [today] = useState(() => todayIsoDay(new Date()));
  const appliedFilters = appliedFilterCount(filters);

  const [state, setState] = useState<LoadState>('loading');
  const [result, setResult] = useState<OrderHistoryPage | null>(null);
  /**
   * Bumped by the error state's "Try again". Without it a retry of the SAME depot,
   * page and size is not a dependency change, so the effect would not re-run and a
   * transient failure would strand the screen until the route was navigated away
   * and back. The value itself is never read, only depended on.
   */
  const [retryToken, setRetryToken] = useState(0);

  // The sort and the four filter values join the effect's key rather than replacing the
  // in-flight guard. The filters are read off `filters` as primitives, not as the object,
  // because the object is rebuilt on every parse of the search params and would re-fetch
  // on every render; `sort` is a primitive already.
  const { submittedFrom, submittedTo, orderRef, salesRepOnly } = filters;

  useEffect(() => {
    if (depotId === null) {
      setState('loaded');
      setResult(null);
      return;
    }
    let alive = true;
    setState('loading');
    listOrderHistory({
      depotId,
      customerAccountCode,
      range:
        rangeCode === null || rangeEntryKind === null
          ? null
          : { assortmentCode: rangeCode, entryKind: rangeEntryKind },
      page,
      pageSize,
      sort,
      filters: {
        ...(submittedFrom === null ? {} : { submittedFrom }),
        ...(submittedTo === null ? {} : { submittedTo }),
        ...(orderRef === '' ? {} : { orderRef }),
        ...(salesRepOnly ? { salesRepOnly } : {}),
      },
    })
      .then((loaded) => {
        if (!alive) return;
        setResult(loaded);
        setState('loaded');
      })
      .catch(() => {
        if (!alive) return;
        setResult(null);
        setState('error');
      });
    return () => {
      // A depot switch, a page step, a sort or a filter change mid-flight must not have
      // its response land after the newer one and overwrite it.
      alive = false;
    };
  }, [
    depotId,
    customerAccountCode,
    rangeCode,
    rangeEntryKind,
    page,
    pageSize,
    sort,
    submittedFrom,
    submittedTo,
    orderRef,
    salesRepOnly,
    retryToken,
  ]);

  const meta = result?.page ?? null;
  const orders = result?.orders ?? [];
  /**
   * A page or size change, as opposed to the first load. The catalogue keeps its
   * grid up and disables the pager while navigating; doing the same here means a
   * page step no longer replaces the whole list with "Loading your orders…" and
   * back, and it is what makes the pager's `busy` state reachable at all.
   */
  const navigating = state === 'loading' && result !== null;
  const firstLoad = state === 'loading' && result === null;
  // Shown whenever there is a page to describe, not only when there are several:
  // the per-page control is useful on a single page too, and hiding the footer
  // made the size choice vanish exactly when a shopkeeper wanted to widen it.
  const showPager = meta !== null;

  /**
   * The one writer of the screen's URL state. Every control goes through it, so the
   * canonical-URL rules (no defaults written, paging, sort and filters in one
   * vocabulary) cannot be applied in one place and forgotten in another. Every call
   * states the whole state, so no control can drop another's value by leaving it out.
   *
   * `replace` is for a change that is not a place a user would want to come Back to;
   * everything else pushes, because AC8 requires browser back/forward to move between
   * filter states.
   */
  const updateUrl = useCallback(
    (
      next: {
        page: number;
        pageSize: number;
        sort: OrderHistorySort;
        filters: OrderFilterValues;
      },
      options: { replace: boolean } = { replace: false },
    ) => {
      setSearchParams(
        (previous) => writeOrderHistoryUrlState(previous, next, DEFAULT_PAGE_SIZE),
        options,
      );
    },
    [setSearchParams],
  );

  const goToPage = (next: number) => {
    if (meta === null) return;
    updateUrl({ page: next, pageSize, sort, filters });
  };

  /** A size change resets to page 1: page 3 of the old size is a different window. */
  const changePageSize = (nextSize: number) => {
    updateUrl({ page: 1, pageSize: nextSize, sort, filters });
  };

  /**
   * A sort change resets to page 1 and pushes: page 3 of the old order holds different
   * orders, and browser Back should return to the order the reader came from. The
   * `Listbox` only reports a value that differs from the current one, so re-choosing the
   * current sort adds no history entry.
   */
  const changeSort = (nextSort: OrderHistorySort) => {
    updateUrl({ page: 1, pageSize, sort: nextSort, filters });
  };

  /**
   * A filter change resets to page 1 for the same reason a size change does: the
   * narrowed list is a different result set, and page 3 of the old one is not a window
   * in the new one (it is usually not a window at all).
   */
  const changeFilters = useCallback(
    (nextFilters: OrderFilterValues) => {
      // Re-choosing what is already chosen is not a navigation. Without this, clicking
      // the already-selected day would add a history entry that Back walks through for
      // no visible change.
      if (sameFilters(nextFilters, filters)) return;
      updateUrl({ page: 1, pageSize, sort, filters: nextFilters });
    },
    [filters, pageSize, sort, updateUrl],
  );

  /** AC5: reset every filter and restore the full, unfiltered list, in the chosen sort. */
  const clearFilters = useCallback(() => {
    updateUrl({ page: 1, pageSize, sort, filters: NO_ORDER_FILTERS });
  }, [pageSize, sort, updateUrl]);

  return (
    <div className={styles.page}>
      <CatalogueLinkingHeader showManagerDashboard={isAdminRole(role)} />

      <main className={styles.main}>
        <div className={styles.pageHead}>
          <div className={styles.pageHeadText}>
            <h1 className={styles.pageTitle}>Previous orders</h1>
            <div className={styles.pageSubline}>
              {/* The same label the header's depot switch shows (`depotNameFor`). It is
                  undefined only with no active depot, where there is no depot to name. */}
              {depotName !== undefined && (
                <span
                  className={styles.depotPill}
                  data-testid="order-history-depot"
                  data-accent={depotAccent}
                >
                  {depotAccent !== undefined && (
                    <img
                      src={depotIconSrc(depotAccent)}
                      width={16}
                      height={16}
                      alt=""
                      className={styles.depotPillIcon}
                    />
                  )}
                  <span className={styles.depotPillLabel}>{depotName}</span>
                </span>
              )}
              <p className={styles.pageSubtitle}>Expand a row to review what was ordered.</p>
            </div>
          </div>
          {/* A navigation, so a link rather than a button: it must open in a new tab
              on a middle click and show its target on hover like any other route. */}
          {mobile ? (
            // Mobile draws it as a text link under the subtitle (Figma 5419:23790).
            <Link to="/products" className={styles.returnTextLink}>
              <Icon name="arrowLeft" size={14} />
              Return to products
            </Link>
          ) : (
            <Link to="/products" className={styles.returnLink}>
              Return to products
            </Link>
          )}
        </div>

        {depotId === null ? (
          <div className={styles.empty}>
            <p className={styles.emptyTitle}>Choose a depot to see your orders.</p>
            <p className={styles.emptyHint}>
              Pick a depot from the menu at the top of the page and the orders you have sent for it
              will appear here.
            </p>
          </div>
        ) : state === 'error' ? (
          <Banner type="error" title="Order history unavailable">
            We could not load your orders.{' '}
            <button
              type="button"
              className={styles.retryButton}
              onClick={() => setRetryToken((token) => token + 1)}
            >
              Try again
            </button>
          </Banner>
        ) : (
          <>
            {/* Story 9.6 (ADO-1080): the Filters panel sits above the list and OUTSIDE
                every load state below it, so a filter set stays on screen while its own
                results are loading and, above all, while they come back empty — a panel
                that disappeared with its results would leave the AC6 no-results state
                with no way to widen the search. */}
            <OrderFilters
              values={filters}
              onChange={changeFilters}
              onClear={clearFilters}
              today={today}
              presentation={mobile ? 'sheet' : 'panel'}
            />

            {firstLoad ? (
              <p className={styles.loading} role="status">
                Loading your orders…
              </p>
            ) : orders.length === 0 ? (
              // AC6 vs Story 9.4's AC6: two different facts, two different states. "No
              // order matches these filters" is about the filters and offers a way out
              // of them; "you have not sent any orders yet" is about the account and
              // would be a lie in front of an account with a hundred orders and a
              // narrow date range.
              appliedFilters > 0 ? (
                <div className={styles.empty} role="status">
                  <p className={styles.emptyTitle}>No orders match these filters.</p>
                  <p className={styles.emptyHint}>
                    Try a wider date range or a different order reference, or clear the filters to
                    see every order for this depot.
                  </p>
                  <Button variant="secondary" onClick={clearFilters}>
                    Clear filters
                  </Button>
                </div>
              ) : (
                <div className={styles.empty}>
                  <p className={styles.emptyTitle}>You have not sent any orders yet.</p>
                  <p className={styles.emptyHint}>
                    Orders appear here as soon as you send them, newest first. Return to products to
                    start your first one.
                  </p>
                </div>
              )
            ) : (
              <OrderList
                orders={orders}
                meta={meta}
                showPager={showPager}
                navigating={navigating}
                sort={sort}
                onSortChange={changeSort}
                onPageChange={goToPage}
                onPageSizeChange={changePageSize}
                layout={layout}
                appliedFilters={appliedFilters}
                onClearFilters={clearFilters}
                customerAccountCode={customerAccountCode}
                cartName={depotName}
              />
            )}
          </>
        )}
      </main>
    </div>
  );
}

/** The Sort bar, the card list and the pager. Separate from the screen so the filter
 *  panel above can sit outside the load-state branch, and so the Sort bar renders only
 *  when there is a list to order. */
function OrderList({
  orders,
  meta,
  showPager,
  navigating,
  sort,
  onSortChange,
  onPageChange,
  onPageSizeChange,
  layout,
  appliedFilters,
  onClearFilters,
  customerAccountCode,
  cartName,
}: {
  orders: readonly OrderSummary[];
  meta: OrderHistoryPage['page'] | null;
  showPager: boolean;
  navigating: boolean;
  sort: OrderHistorySort;
  onSortChange: (sort: OrderHistorySort) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  layout: HeaderLayout;
  appliedFilters: number;
  onClearFilters: () => void;
  /** Story 8.4 (ADO-2013): the account the list was read for. */
  customerAccountCode: string | null;
  /** Story 8.3 (ADO-2012), card 07: the cart's name, as the page heading shows it. */
  cartName: string | undefined;
}) {
  const mobile = layout === 'mobile';
  // ADO-1779: the mobile and tablet frames add the applied-filter count to the range
  // ("Showing 1 of 3 · 4 filters applied", 5415:62740 and 5416:23793).
  const showing =
    meta === null
      ? null
      : layout !== 'desktop' && appliedFilters > 0
        ? `${showingRange(meta)} · ${appliedFilters} ${appliedFilters === 1 ? 'filter' : 'filters'} applied`
        : showingRange(meta);
  const sortControl = (
    <Listbox
      label="Sort by"
      options={SORT_OPTIONS}
      value={sort}
      onChange={onSortChange}
      id="orders-sort"
      className={styles.sortControl}
      presentation={mobile ? 'sheet' : 'popup'}
    />
  );

  return (
    <>
      {mobile ? (
        // Mobile (Figma 5412:21698, 5415:62739): Sort by full width under the Filters
        // button, then the range over the list, with Clear filters on its own line at
        // the right, because the sheet the filters live in is closed by then.
        <>
          {sortControl}
          <div className={styles.listHead}>
            {showing !== null && <p className={styles.showing}>{showing}</p>}
            {appliedFilters > 0 && (
              <button type="button" className={styles.listClear} onClick={onClearFilters}>
                Clear filters
              </button>
            )}
          </div>
        </>
      ) : (
        <div className={styles.sortBar}>
          {showing !== null && <p className={styles.showing}>{showing}</p>}
          {sortControl}
        </div>
      )}

      <ul className={styles.list} aria-label="Previous orders">
        {orders.map((order) => (
          <li key={order.orderId} className={styles.card}>
            <OrderCard
              order={order}
              mobile={mobile}
              customerAccountCode={customerAccountCode}
              cartName={cartName}
            />
          </li>
        ))}
      </ul>

      {showPager && meta !== null && (
        <Pagination
          page={meta.page}
          totalPages={meta.totalPages}
          pageSize={meta.pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          busy={navigating}
          idPrefix="orders"
        />
      )}
    </>
  );
}

type DetailState = 'idle' | 'loading' | 'loaded' | 'error';

/** The last depot (and account) the list was read for; see the page reset in `OrderHistory`. */
interface RealDepot {
  readonly depotId: string;
  readonly customerAccountCode: string | null;
  /** Enabler E8-G (ADO-3109): the cart's range, `null` before the ordering choices land. */
  readonly rangeCode: string | null;
}

/**
 * One order row: the collapsed summary (Story 9.4) plus the Story 9.5 expander and the
 * inline detail accordion. The detail is fetched ONCE, on the first expand: a later
 * collapse/expand of the same order shows the cached lines, and an error offers a retry.
 * The reference identifies the accordion for the toggle's `aria-controls`.
 */
function OrderCard({
  order,
  mobile,
  customerAccountCode,
  cartName,
}: {
  order: OrderSummary;
  mobile: boolean;
  /** Story 8.3 (ADO-2012), card 07: the cart's name as the page shows it, when known. */
  cartName: string | undefined;
  /** Story 8.4 (ADO-2013): the account the list was read for, sent with the detail and the
   *  tracking reads. */
  customerAccountCode: string | null;
}) {
  const reference = order.customerOrderReference ?? order.orderReference ?? order.orderId;
  const [expanded, setExpanded] = useState(false);
  const [detailState, setDetailState] = useState<DetailState>('idle');
  const [detail, setDetail] = useState<OrderDetail | null>(null);
  const panelId = `order-detail-${order.orderId}`;
  // Story 8.7 (ADO-2016), D-12: a pre-sell order was not built in a cart, so it is not
  // offered as one; the server refuses it too.
  const reAddable = order.origin !== 'PRESELL';
  // Owned here rather than by the control: the control renders in the head's action cell
  // or in its own row depending on the layout, so it remounts when the viewport crosses
  // 768px, and its cooldown must not reset with it (a second tab could then open).
  const tracking = useTrackOrder(order.orderId, customerAccountCode);

  /**
   * Mounted flag for the detail fetch, the same `alive` discipline the list-fetch effect
   * above already uses. This card is keyed by order id inside a list the parent re-fetches
   * on a depot switch or a page step, so it can unmount with a detail request still in
   * flight; without this the late response would set state on an unmounted card. Assigned
   * in the effect body (not just the cleanup) so a remount re-arms it.
   */
  const aliveRef = useRef(true);
  /** True while a detail request is outstanding — see `loadDetail`. */
  const inFlightRef = useRef(false);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const loadDetail = useCallback(() => {
    // The error state's "Try again" can be clicked again before the re-render hides it.
    // Letting the second click through would put two requests for the same order in
    // flight, whose responses could then settle out of order; one at a time removes the
    // race rather than trying to order the results afterwards.
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setDetailState('loading');
    getOrderDetail(order.orderId, customerAccountCode)
      .then((loaded) => {
        inFlightRef.current = false;
        if (!aliveRef.current) return;
        setDetail(loaded);
        setDetailState('loaded');
      })
      .catch(() => {
        inFlightRef.current = false;
        if (!aliveRef.current) return;
        setDetailState('error');
      });
  }, [order.orderId, customerAccountCode]);

  const toggle = () => {
    setExpanded((wasExpanded) => {
      // Fetch on the FIRST expand only. `idle` means never loaded; a reopen after a
      // successful load reuses the cached lines, and a reopen after an error keeps the
      // error's own "Try again" as the way to retry.
      if (!wasExpanded && detailState === 'idle') loadDetail();
      return !wasExpanded;
    });
  };

  return (
    <>
      <div className={styles.cardRow}>
        {/* The summary packs left on column tracks every card shares; the actions keep
            to the right edge. The fields read as the order's own facts, not as column titles
            for the line table the expanded card shows beneath them. */}
        <div className={styles.summary}>
          <span className={styles.reference}>{reference}</span>
          <Field label="Order date">
            <time dateTime={order.submittedAt}>{formatSubmittedDate(order.submittedAt)}</time>
          </Field>
          {/* AC3: the row NAMES the sender. The Auth0 subject is the fallback for a
              user erased since the order was sent, so the row still says who sent it
              in the only terms left rather than going blank. */}
          <Field label="Placed by">{order.placedByName ?? order.placedByUserId}</Field>
          <Field label="Total">{formatMoney(order.total)}</Field>
          <Field label="Deposit total">{formatMoney(order.depositTotal)}</Field>
        </div>

        <div className={styles.rowActions}>
          {!mobile && <TrackOrderControl trackable={order.trackable} tracking={tracking} />}
          <button
            type="button"
            className={styles.expander}
            aria-expanded={expanded}
            aria-controls={panelId}
            onClick={toggle}
          >
            {/* The visible label is the chevron; the action is named for assistive tech,
                and it carries the order reference so it is unique down the list. */}
            <span className={styles.expanderLabel}>
              {expanded ? 'Hide' : 'Show'} details for order {reference}
            </span>
            <Icon
              name="caretDown"
              size={20}
              aria-hidden
              className={expanded ? styles.expanderIconOpen : styles.expanderIcon}
            />
          </button>
        </div>
      </div>

      {/* Mobile (Figma 5417:62249): the Track control takes its own full-width row under
          the head, so it stays reachable with the panel shut (Story 9.3 AC1). */}
      {mobile && <TrackOrderControl trackable={order.trackable} tracking={tracking} fullWidth />}

      {/* Rendered always so `aria-controls` always resolves; collapsed until expanded, through
          `Collapse` (ADO-2262), which keeps it mounted and inert while closed. */}
      <Collapse id={panelId} open={expanded} className={styles.detailCollapse}>
        <div className={styles.detailPanel}>
          {detailState === 'loading' && (
            <p className={styles.detailLoading} role="status">
              Loading order details…
            </p>
          )}
          {detailState === 'error' && (
            <div className={styles.detailError}>
              <Banner type="error" title="Order details unavailable">
                We could not load this order.{' '}
                <button type="button" className={styles.retryButton} onClick={loadDetail}>
                  Try again
                </button>
              </Banner>
            </div>
          )}
          {detailState === 'loaded' &&
            detail !== null &&
            (mobile ? (
              <OrderDetailBlocks detail={detail} reference={reference} />
            ) : (
              <OrderDetailLines detail={detail} reference={reference} />
            ))}
          {reAddable && (
            <ReAddBand
              {...(cartName === undefined ? {} : { cartName })}
              orderId={order.orderId}
              reference={reference}
              total={order.total}
              ready={
                detailState === 'loaded' &&
                detail !== null &&
                detail.lines.some((line) => line.quantity > 0)
              }
            />
          )}
        </div>
      </Collapse>
    </>
  );
}

/**
 * The expanded order's line table and its "Total cost" footer.
 *
 * A hand-rolled semantic table rather than the design-system `Table` primitive, for two
 * reasons that primitive cannot express: the "Total cost" row belongs in a `<tfoot>`
 * spanning the money columns, and a line key must stay stable when the same SKU appears
 * on more than one line (the `Table` keys by a single row value). It keeps the
 * primitive's overflow-x wrapper so the page body never scrolls sideways on a narrow
 * viewport.
 */
function OrderDetailLines({ detail, reference }: { detail: OrderDetail; reference: string }) {
  return (
    <>
      <div className={styles.detailScroll}>
        <table className={styles.detailTable}>
          <caption className={styles.visuallyHidden}>Lines on order {reference}</caption>
          {/* Name takes the slack; the five figure columns share one width (`.numCol`). */}
          <colgroup>
            <col />
            <col className={styles.numCol} />
            <col className={styles.numCol} />
            <col className={styles.numCol} />
            <col className={styles.numCol} />
            <col className={styles.numCol} />
          </colgroup>
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col" className={styles.num}>
                Price
              </th>
              <th scope="col" className={styles.num}>
                Deposit
              </th>
              <th scope="col" className={styles.num}>
                Qty
              </th>
              <th scope="col" className={styles.num}>
                Deposit total
              </th>
              <th scope="col" className={styles.num}>
                Line total
              </th>
            </tr>
          </thead>
          <tbody>
            {detail.lines.length === 0 ? (
              <tr>
                <td colSpan={6} className={styles.detailEmpty}>
                  This order has no lines to show.
                </td>
              </tr>
            ) : (
              detail.lines.map((line, index) => (
                <LineRow key={`${line.skuCode}#${index}`} line={line} />
              ))
            )}
          </tbody>
          <tfoot>
            <tr>
              {/* Label and value sit together in the Line total column (the frame's one
                  "Total cost  €86.88" run), so the leading columns carry no visible text.
                  The row header itself is not dropped: it moves here, visually hidden, so
                  the total keeps its `<th scope="row">` association for assistive tech.
                  `.visuallyHidden` sits on the INNER span, not the `<th>` itself: that class
                  is `position: absolute`, which blockifies a table cell and drops it out of
                  the table's column grid, so `colSpan` would stop reserving anything and the
                  value `<td>` would land in column 1 instead of under Line total. */}
              <th scope="row" colSpan={5}>
                <span className={styles.visuallyHidden}>Total cost</span>
              </th>
              <td className={styles.num}>
                <span className={styles.totalCostLabel} aria-hidden="true">
                  Total cost
                </span>
                {formatMoney(detail.total)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
}

interface TrackOrderState {
  readonly busy: boolean;
  readonly failed: boolean;
  readonly open: () => void;
}

/**
 * The tracking request and its cooldown, held by the order card (see `OrderCard`). On open it
 * fetches the external deep link and opens it in a new tab (`window.open(..., '_blank')`, SL1.0
 * parity), with the opener severed since the target is an external site.
 *
 * A short post-click cooldown ports SL1.0's `btn_track_order` debounce: the control disables for a
 * few seconds so a double click cannot open two tabs or fire two requests. The timer is cleared on
 * unmount (the card unmounts on a depot switch or page step).
 */
function useTrackOrder(orderId: string, customerAccountCode: string | null): TrackOrderState {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const cooldownRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The card unmounts on a depot switch or page step (see OrderCard's own `aliveRef`), which can
  // happen with a tracking request still in flight. Guard every post-await state update and the
  // cooldown timer against a settle-after-unmount, the same discipline `OrderCard.loadDetail` uses.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      if (cooldownRef.current !== null) clearTimeout(cooldownRef.current);
    };
  }, []);

  const open = () => {
    if (busy) return;
    setBusy(true);
    setFailed(false);
    // Open the tab SYNCHRONOUSLY, inside the click's user-activation window, then navigate it once the
    // URL resolves. A `window.open` AFTER the await has lost the activation gesture and Safari (and
    // Chrome on a slow fetch) block it as a popup. `noopener`/`noreferrer` in the features string
    // would null the handle we need to navigate, so the opener is severed manually instead; a referrer
    // to BWG's own site is acceptable (and SL1.0 parity).
    const tab = window.open('', '_blank');
    if (tab !== null) tab.opener = null;
    getOrderTrackingUrl(orderId, customerAccountCode)
      .then(({ trackingUrl }) => {
        if (tab === null) {
          // The browser blocked the tab. Nothing opened, so surface a retryable error rather than
          // sitting silently in a cooldown (the failure the reviewer flagged).
          if (aliveRef.current) {
            setFailed(true);
            setBusy(false);
          }
          return;
        }
        tab.location.href = trackingUrl;
        if (!aliveRef.current) return;
        // SL1.0 `btn_track_order` debounce: a tab DID open, so hold the control disabled briefly so a
        // double click cannot open a second tab. Armed only on success — see the catch below.
        cooldownRef.current = setTimeout(() => {
          if (aliveRef.current) setBusy(false);
        }, 4000);
      })
      .catch(() => {
        // The fetch failed: close the blank tab we opened so it does not sit on about:blank, and
        // re-enable immediately so the user can act on the "please try again" message.
        if (tab !== null) tab.close();
        if (!aliveRef.current) return;
        setFailed(true);
        setBusy(false);
      });
  };

  return { busy, failed, open };
}

/**
 * Story 9.3 (ADO-1081), AC1 — the "Track order" hand-off to `bwg.depot.guru`.
 *
 * Renders ONLY when the order is track-eligible (order-intrinsic `trackable`) AND the caller is not a
 * supplier user. The supplier hide is DISPLAY ONLY (NFR18): the tracking endpoint re-checks both and
 * is authoritative, so a supplier who forced the request still gets a 404. The request and its
 * cooldown are `useTrackOrder`'s.
 */
function TrackOrderControl({
  trackable,
  tracking,
  fullWidth = false,
}: {
  trackable: boolean;
  tracking: TrackOrderState;
  /** The mobile card's own full-width row (Figma 5417:62249). */
  fullWidth?: boolean;
}) {
  const role = useSessionStore((s) => s.role);
  const { busy, failed, open: onClick } = tracking;

  // Hidden for a non-trackable order and for supplier users. The server is the authoritative gate.
  if (!trackable || isSupplierRole(role)) return null;

  return (
    <div className={fullWidth ? styles.trackRow : styles.trackControl}>
      <Button
        variant="secondary"
        onClick={onClick}
        disabled={busy}
        fullWidth={fullWidth}
        className={styles.trackButton}
      >
        {busy ? 'Opening tracking…' : 'Track order'}
      </Button>
      {failed && (
        <span className={styles.trackError} role="alert">
          Could not open tracking just now. Please try again.
        </span>
      )}
    </div>
  );
}

/**
 * ADO-1779: the expanded order on mobile (Figma 5412:21745): one block per line, the
 * name over its code and then the five figures as labelled pairs that wrap three to a
 * row, closed by the "Total cost" row. A 320px card cannot hold the six-column table
 * (it scrolled sideways under the name), so the same figures are listed rather than
 * tabulated: a list of lines, each with a description list of its figures.
 */
function OrderDetailBlocks({ detail, reference }: { detail: OrderDetail; reference: string }) {
  return (
    <>
      {detail.lines.length === 0 ? (
        <p className={styles.blockEmpty}>This order has no lines to show.</p>
      ) : (
        <ul className={styles.blockList} aria-label={`Lines on order ${reference}`}>
          {detail.lines.map((line, index) => {
            const hasName = line.productName !== undefined;
            return (
              <li key={`${line.skuCode}#${index}`} className={styles.block}>
                <span className={styles.lineName}>{hasName ? line.productName : line.skuCode}</span>
                <span className={styles.lineCode}>
                  {hasName ? line.skuCode : 'product no longer listed'}
                </span>
                <dl className={styles.blockStats}>
                  <BlockStat label="Price">{formatMoney(line.unitPrice)}</BlockStat>
                  <BlockStat label="Deposit">{formatMoney(line.drsRate)}</BlockStat>
                  <BlockStat label="Qty">{line.quantity}</BlockStat>
                  <BlockStat label="Deposit total">{formatMoney(line.lineDepositTotal)}</BlockStat>
                  <BlockStat label="Line total">{formatMoney(line.lineTotal)}</BlockStat>
                </dl>
              </li>
            );
          })}
        </ul>
      )}
      <p className={styles.blockTotal}>
        <span className={styles.totalCostLabel}>Total cost</span>
        {formatMoney(detail.total)}
      </p>
    </>
  );
}

function BlockStat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={styles.blockStat}>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function LineRow({ line }: { line: OrderDetailLine }) {
  // Name over code (the frame). The name is absent only when the SKU has no catalogue row
  // at all (a product delisted since the order was sent still resolves the name it was
  // ordered under), so the code becomes the label and the sub-line says why, rather than
  // dropping the line: the order keeps every figure it was sent with.
  const hasName = line.productName !== undefined;
  return (
    <tr>
      <td>
        <span className={styles.lineName}>{hasName ? line.productName : line.skuCode}</span>
        <span className={styles.lineCode}>
          {hasName ? line.skuCode : 'product no longer listed'}
        </span>
      </td>
      <td className={styles.num}>{formatMoney(line.unitPrice)}</td>
      <td className={styles.num}>{formatMoney(line.drsRate)}</td>
      <td className={styles.num}>{line.quantity}</td>
      <td className={styles.num}>{formatMoney(line.lineDepositTotal)}</td>
      <td className={styles.num}>{formatMoney(line.lineTotal)}</td>
    </tr>
  );
}

/** One labelled summary field: a small label over its value (Figma "Order cards"). */
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      <span className={styles.fieldValue}>{children}</span>
    </div>
  );
}

/**
 * "Showing 11–20 of 23": the first and last row of this page and the total, from the
 * page metadata. Rendered only with a non-empty list, and the server clamps `page` to
 * the real range, so the first row is always within the total. An en dash, because it is
 * a range.
 */
function showingRange(meta: OrderHistoryPage['page']): string {
  const first = (meta.page - 1) * meta.pageSize + 1;
  const last = Math.min(meta.page * meta.pageSize, meta.total);
  return `Showing ${first}–${last} of ${meta.total}`;
}
